import {
  transformUnitsApiResponseToClient,
  type UnitHitApiResponse,
  type UnitSourceApiResponse,
} from "@superbright/indexeddb-orm";

import type {
  ClosestSectionClient,
  ListingsClientResponse,
  ResultUnit,
} from "features/results/types/results.types";

import { enrichListingUnit } from "./enrichListingUnit";

type ListingsTierApi = {
  hits?: UnitHitApiResponse<UnitSourceApiResponse>[];
  total?: number;
  match_tier?: string;
  sections?: Array<{
    dimension: string;
    headline: string;
    total?: number;
    hits?: UnitHitApiResponse<UnitSourceApiResponse>[];
  }>;
};

type ListingsApiData = {
  imageSignature?: string;
  messaging?: ListingsClientResponse["messaging"];
  all?: ListingsTierApi;
  best_fits?: ListingsTierApi;
  closest?: ListingsTierApi;
};

/** Maps one API tier (OpenSearch hits) to client units with image URLs, etc. */
const transformTier = (
  tier: ListingsTierApi | undefined,
  propertySlug: string,
  imageSignature?: string
) => {
  const transformed = transformUnitsApiResponseToClient(
    {
      hits: tier?.hits,
      total: { value: tier?.total ?? 0 },
      imageSignature,
    },
    propertySlug
  );
  return {
    total: transformed.total,
    hits: transformed.hits.map((hit) =>
      enrichListingUnit(hit as Record<string, unknown>, imageSignature)
    ) as unknown as ResultUnit[],
  };
};

const transformClosestSections = (
  sections: ListingsTierApi["sections"],
  propertySlug: string,
  imageSignature?: string
): ClosestSectionClient[] =>
  (sections ?? []).map((section) => {
    const tier = transformTier(
      { hits: section.hits, total: section.total },
      propertySlug,
      imageSignature
    );
    return {
      dimension: section.dimension,
      headline: section.headline,
      total: tier.total,
      hits: tier.hits,
    };
  });

/**
 * Adapts listings API payload to embed client shape:
 * - snake_case tier keys → camelCase (`best_fits` → `bestFits`, `match_tier` → `matchTier`)
 * - each tier's hits via `transformUnitsApiResponseToClient` (flatten `_source`, build image URLs)
 */
export function transformListingsApiResponse(
  data: ListingsApiData,
  propertySlug: string
): ListingsClientResponse {
  const imageSignature = data.imageSignature;
  const all = transformTier(data.all, propertySlug, imageSignature);
  const bestFits = transformTier(data.best_fits, propertySlug, imageSignature);
  const closestSections = transformClosestSections(
    data.closest?.sections,
    propertySlug,
    imageSignature
  );

  // Prioritise closest.hits from the API (flat list) over sections.
  // If the API provides hits directly, use them; otherwise leave empty
  // so the view renders per-dimension sections.
  const closestDirectHits =
    data.closest?.hits && data.closest.hits.length > 0
      ? transformTier(data.closest, propertySlug, imageSignature).hits
      : [];
  const closestHitsFromSections = closestSections.flatMap((s) => s.hits);
  const closestTotal =
    data.closest?.total ??
    (closestDirectHits.length > 0
      ? closestDirectHits.length
      : closestHitsFromSections.length);

  return {
    messaging: data.messaging ?? null,
    all,
    bestFits,
    closest: {
      total: closestTotal,
      hits: closestDirectHits,
      matchTier: data.closest?.match_tier,
      sections: closestSections,
    },
  };
}
